import loadable from '@loadable/component';

import {
  CONFIRM_EMAIL,
  HOME,
  LOGIN,
  REGISTER,
  RESET_PASSWORD,
  RESET_PASSWORD_RAW,
  PROMOTIONS,
  PROMOTION_DETAIL,
  LOAD_USER,
  COUPONS_LIST,
  RECOVER_CREDITS,
  CREATE_ORDER,
  POST_ROUTER,
  REDEEM_COUPON,
  CANCEL_COUPON,
  TELEPHONE_FORM,
  TELEPHONE_FORM_PROMO,
  PROFILE_PARAMETER,
  PROFILE,
  VERIFY_PHONE,
  SURVEY_PRE_POST,
  SURVEY_EXTRA,
  SURVEY_SIMULATOR,
  ROUTER_NEXT_PRE,
  ROUTER_NEXT_POST,
  LEGAL_CONDITIONS,
  PRIVACY_POLICY,
  COOKIES_POLICY,
  STATIC_CONTENT_REMOTE,
  FORCE_ERROR,
  CODE_READER,
  RR_PAGE,
  ADDRESS_EDIT,
  CALLBACK_GOOGLE,
  REGISTER_EXTERNAL,
  UNSUB,
} from '@utils/paths';

const Root = loadable(() => import(/* webpackChunkName: "Root" */ '@layouts/Root'));
const Home = loadable(() => import(/* webpackChunkName: "Home" */ '@pages/Home'));
const NotFound = loadable(() => import(/* webpackChunkName: "NotFound" */ '@pages/NotFound'));
const MainLayout = loadable(() => import(/* webpackChunkName: "MainLayout" */ '@layouts/MainLayout'));
const PlainLayout = loadable(() => import(/* webpackChunkName: "PlainLayout" */ '@layouts/PlainLayout'));

const LoadUserPage = loadable(() => import(/* webpackChunkName: "LoadUserPage" */ '@pages/LoadUser'));
const CookiesPolicyPage = loadable(
  () => import(/* webpackChunkName: "StaticContentPage" */ '@components/StaticGenerator/CookiesPolicy')
);
const StaticRemotePage = loadable(
  () => import(/* webpackChunkName: "StaticRemotePage" */ '@components/StaticGenerator/Remote')
);

const SignInPage = loadable(() => import(/* webpackChunkName: "SignInPage" */ '@pages/SignIn'));
const SignUpPage = loadable(() => import(/* webpackChunkName: "SignInPage" */ '@pages/SignUp'));
const ResetPasswordPage = loadable(() => import(/* webpackChunkName: "ResetPasswordPage" */ '@pages/ResetPassword'));
const ConfirmPasswordPage = loadable(() => import(/* webpackChunkName: "ConfirmPassword" */ '@pages/ConfirmPassword'));
const ConfirmEmailPage = loadable(() => import(/* webpackChunkName: "ConfirmEmail" */ '@pages/ConfirmEmail'));

const NextPromotionsPage = loadable(() => import(/* webpackChunkName: "NextPromotionsPage" */ '@pages/NextPromotions'));
const NextPromotionDetailPage = loadable(
  () => import(/* webpackChunkName: "NextPromotionDetailPage" */ '@pages/NextPromotion')
);

const RouterPage = loadable(() => import(/* webpackChunkName: "RouterPage" */ '@pages/Router'));

const CouponsPage = loadable(() => import(/* webpackChunkName: "CouponsPage" */ '@pages/Coupons'));
const RedeemCouponPage = loadable(() => import(/* webpackChunkName: "RedeemCouponPage" */ '@pages/RedeemCoupon'));
const CancelCouponPage = loadable(() => import(/* webpackChunkName: "CancelCouponPage" */ '@pages/CancelCoupon'));
const CreatePromo = loadable(() => import(/* webpackChunkName: "CreatePromo" */ '@pages/CreatePromo'));
const PostRouter = loadable(() => import(/* webpackChunkName: "PostRouter" */ '@pages/PostRouter'));
const CreditsPage = loadable(() => import(/* webpackChunkName: "CreditsPage" */ '@pages/Credits'));
const Profile = loadable(() => import(/* webpackChunkName: "ProfilePage" */ '@pages/Profile/AccountSettings'));
const Profile2Steps = loadable(() => import(/* webpackChunkName: "ProfilePage" */ '@pages/Profile/TwoSteps'));
const TelephonePage = loadable(() => import(/* webpackChunkName: "TelephonePage" */ '@pages/Profile/Telephone'));
const VerifyTelephonePage = loadable(() => import(/* webpackChunkName: "VerifyPage" */ '@pages/Profile/VerifyPhone'));

const SurveyPrePostPage = loadable(() => import(/* webpackChunkName: "SurveyPrePostPage" */ '@pages/Survey/PrePost'));
const SurveyExtraPage = loadable(() => import(/* webpackChunkName: "SurveyExtraPage" */ '@pages/Survey/Extra'));
const SurveySimulatorPage = loadable(
  () => import(/* webpackChunkName: "SurveySimulatorPage" */ '@pages/Survey/Simulator')
);
const RatingAndReviewPage = loadable(
  () => import(/* webpackChunkName: "RatingAndReviewPage" */ '@pages/RatingAndReview')
);

const ForceErrorPage = loadable(() => import(/* webpackChunkName: "ForceErrorPage" */ '@pages/ForceError'));
const CodeReaderPage = loadable(() => import(/* webpackChunkName: "CodeReaderPage" */ '@pages/CodeReader'));
const AddressEditPage = loadable(
  () => import(/* webpackChunkName: "CodeReaderPage" */ '@pages/Profile/AccountSettings/AddressPage')
);

const ExternalCallbackPage = loadable(() => import(/* webpackChunkName: "SignInPage" */ '@pages/externalCallback'));
const SignUpSsoPage = loadable(() => import(/* webpackChunkName: "SignInPage" */ '@pages/SignUpSso'));
const UnsubPage = loadable(() => import(/* webpackChunkName: "SignInPage" */ '@pages/Unsub'));

const routes = [
  {
    component: Root,
    routes: [
      {
        path: [
          HOME,
          LOGIN,
          REGISTER,
          RESET_PASSWORD_RAW,
          RESET_PASSWORD,
          CONFIRM_EMAIL,
          PROMOTIONS,
          PROMOTION_DETAIL,
          COUPONS_LIST,
          RECOVER_CREDITS,
          REDEEM_COUPON,
          CANCEL_COUPON,
          LOAD_USER,
          PROFILE_PARAMETER,
          PROFILE,
          TELEPHONE_FORM,
          TELEPHONE_FORM_PROMO,
          VERIFY_PHONE,
          SURVEY_PRE_POST,
          SURVEY_EXTRA,
          ROUTER_NEXT_PRE,
          ROUTER_NEXT_POST,
          LEGAL_CONDITIONS,
          PRIVACY_POLICY,
          COOKIES_POLICY,
          FORCE_ERROR,
          RR_PAGE,
          STATIC_CONTENT_REMOTE,
          ADDRESS_EDIT,
          CALLBACK_GOOGLE,
          REGISTER_EXTERNAL,
          UNSUB,
        ],
        exact: true,
        component: MainLayout,
        routes: [
          {
            path: HOME,
            exact: true,
            component: Home,
            withoutFooter: true,
          },
          {
            path: LOGIN,
            exact: true,
            component: SignInPage,
          },
          {
            path: CALLBACK_GOOGLE,
            exact: true,
            component: ExternalCallbackPage,
          },
          {
            path: REGISTER_EXTERNAL,
            exact: true,
            component: SignUpSsoPage,
          },
          {
            path: REGISTER,
            exact: true,
            component: SignUpPage,
          },
          {
            path: RESET_PASSWORD_RAW,
            exact: true,
            component: ResetPasswordPage,
          },
          {
            path: RESET_PASSWORD,
            exact: true,
            component: ConfirmPasswordPage,
            withoutFooter: true,
          },
          {
            path: CONFIRM_EMAIL,
            exact: true,
            component: ConfirmEmailPage,
            withoutFooter: true,
          },
          {
            path: PROMOTIONS,
            exact: true,
            component: NextPromotionsPage,
            withoutFooter: true,
          },
          {
            path: PROMOTION_DETAIL,
            exact: true,
            component: NextPromotionDetailPage,
            withoutFooter: true,
          },
          {
            path: COUPONS_LIST,
            exact: true,
            component: CouponsPage,
          },
          {
            path: RECOVER_CREDITS,
            exact: true,
            component: CreditsPage,
          },
          {
            path: REDEEM_COUPON,
            exact: true,
            component: RedeemCouponPage,
          },
          {
            path: CANCEL_COUPON,
            exact: true,
            component: CancelCouponPage,
          },
          {
            path: PROFILE,
            exact: true,
            component: Profile,
          },
          {
            path: ADDRESS_EDIT,
            exact: true,
            component: AddressEditPage,
          },
          {
            path: PROFILE_PARAMETER,
            exact: true,
            component: Profile2Steps,
          },
          {
            path: LOAD_USER,
            exact: true,
            component: LoadUserPage,
            withoutFooter: true,
          },
          {
            path: TELEPHONE_FORM,
            exact: true,
            component: TelephonePage,
          },
          {
            path: TELEPHONE_FORM_PROMO,
            exact: true,
            component: TelephonePage,
          },
          {
            path: VERIFY_PHONE,
            exact: true,
            component: VerifyTelephonePage,
          },
          {
            path: SURVEY_PRE_POST,
            exact: true,
            component: SurveyPrePostPage,
          },
          {
            path: SURVEY_EXTRA,
            exact: true,
            component: SurveyExtraPage,
          },
          {
            path: ROUTER_NEXT_PRE,
            exact: true,
            component: RouterPage,
          },
          {
            path: ROUTER_NEXT_POST,
            exact: true,
            component: RouterPage,
          },
          {
            path: COOKIES_POLICY,
            exact: true,
            component: CookiesPolicyPage,
          },
          {
            path: FORCE_ERROR,
            exact: true,
            component: ForceErrorPage,
          },
          {
            path: RR_PAGE,
            exact: true,
            component: RatingAndReviewPage,
          },
          {
            path: STATIC_CONTENT_REMOTE,
            exact: true,
            component: StaticRemotePage,
          },
          {
            path: UNSUB,
            exact: true,
            component: UnsubPage,
          },
          {
            path: '*',
            exact: true,
            component: NotFound,
          },
        ],
      },
      {
        path: [CODE_READER],
        exact: true,
        component: PlainLayout,
        routes: [
          {
            path: CODE_READER,
            exact: true,
            component: CodeReaderPage,
          },
          {
            path: '*',
            exact: true,
            component: NotFound,
          },
        ],
      },
      {
        path: SURVEY_SIMULATOR,
        exact: true,
        component: SurveySimulatorPage,
      },
      {
        path: CREATE_ORDER,
        exact: true,
        component: CreatePromo,
      },
      {
        path: POST_ROUTER,
        exact: true,
        component: PostRouter,
      },
      {
        path: '*',
        component: MainLayout,
        routes: [
          {
            path: '*',
            component: NotFound,
          },
        ],
      },
    ],
  },
];

export { routes };
